import { createContext, useContext } from "solid-js";
import { en } from "./i18n/en";
import { es } from "./i18n/es";
import { uk } from "./i18n/uk";
import { ru } from "./i18n/ru";
import { ht } from "./i18n/ht";
import { ko } from "./i18n/ko";
import { ja } from "./i18n/ja";
import { zh } from "./i18n/zh";

// One file per patient locale under ./i18n, held to the server's list by
// spec/config/patient_languages_spec.rb.
const translations = { en, es, uk, ru, ht, ko, ja, zh };

export type TranslationKey = keyof (typeof translations)["en"];
type Locale = keyof typeof translations;

function translate(
  locale: string,
  key: TranslationKey,
  vars?: Record<string, string | number>,
): string {
  const loc = (locale in translations ? locale : "en") as Locale;
  let text: string = translations[loc][key];
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      text = text.replace(`%{${k}}`, String(v));
    }
  }
  return text;
}

export type TFunction = (key: TranslationKey, vars?: Record<string, string | number>) => string;

const I18nContext = createContext<TFunction>((key) => key);

export const I18nProvider = I18nContext.Provider;

export function useI18n(): TFunction {
  return useContext(I18nContext);
}

export function createT(locale: string): TFunction {
  return (key, vars) => translate(locale, key, vars);
}

// BCP 47 locale for date/time display formatting: the locale itself, except where a region
// picks the right conventions (US Spanish, Korean of Korea, Japanese of Japan, Simplified Chinese). A locale the wizard has no wording
// for falls back to en-US, matching the translation fallback; one browsers have no Intl data for
// (ht) gets its names from localeDate/localeTime (util/date/intl_dates).
const DATE_LOCALES: Record<string, string> = { es: "es-US", ko: "ko-KR", ja: "ja-JP", zh: "zh-CN" };

export function dateLocale(locale: string): string {
  if (!(locale in translations)) return "en-US";
  return DATE_LOCALES[locale] ?? locale;
}

/** Cents to the patient's currency. Deposits are the only money the wizard shows. */
export function formatCurrency(cents: number, locale: string): string {
  const wanted = dateLocale(locale);
  // Intl has no data for some patient locales (ht); asked for one, it formats in the device's language.
  const numberLocale = Intl.NumberFormat.supportedLocalesOf([wanted]).length > 0 ? wanted : "en-US";
  return new Intl.NumberFormat(numberLocale, {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
}
